// ============================================================================
// uiBeta.js: who sees the new client interface.
//
// Oct 13, 2026: half the clients, picked by client code.
// Oct 15, 2026: EVERY client gets the new interface. A client can still go back with
//   "Switch to the classic look" in the name menu (and return with "Try the new look
//   again"), and the classic pages stay in the code until they're retired.
//
// Overrides, on the client's users/<code> record in Firestore:
//   ui_beta: false         always the classic look (set by PrepWorx, clients can't)
//   ui_beta: true          always the new look, ignores the client's switch-back
//   ui_beta_optout: true   the client clicked "Switch to the classic look"
//
// ui_beta wins over everything.
//
// Oct 27, 2026: the classic look is retired. Every client gets the new look; the
//   switch-back menu items are gone and ui_beta_optout / ui_beta: false are ignored.
//   (The classic pages are still in the code; UI_BETA_ON = false below is still the
//   emergency switch that sends everyone back to them.)
// ============================================================================
import { computed } from 'vue'
import usedbStore from '@/stores/db.js'
import useUserStore from '@/stores/user.js'

// Flip to false to send EVERY client back to the classic look at once
// (one-line emergency off switch; push it like any other change).
export const UI_BETA_ON = true

// The one decision every page, the menu and the pop-ups use
export function isBetaUser(user, uid) {
  if (!UI_BETA_ON || !uid) return false
  return true   // Oct 27: everyone (was: ui_beta / ui_beta_optout could keep the classic look)
}

// Could this client use the new interface? (everyone, unless PrepWorx set ui_beta: false)
export function isBetaEligible(user, uid) {
  if (!UI_BETA_ON || !uid) return false
  return true   // Oct 27: everyone
}

export function useUiBeta() {
  const dbStore = usedbStore()
  const userStore = useUserStore()
  const ready = computed(() => !dbStore.stateData.userPending)
  const uid = computed(() => userStore.stateData?.uid)
  const beta = computed(() => ready.value && isBetaUser(dbStore.stateData.user, uid.value))
  const eligible = computed(() => ready.value && isBetaEligible(dbStore.stateData.user, uid.value))
  const optedOut = computed(() => eligible.value && !beta.value && dbStore.stateData.user?.ui_beta_optout === true)
  return { ready, beta, eligible, optedOut, uid }
}

// Menu colors: beta clients get PrepWorx bronze, everyone else keeps the classic green.
// (Used by templates/Default.vue and components/MiniNavigation.vue.)
const GREEN = { navOn: 'border-green-500 text-gray-900', mobOn: 'bg-green-50 border-green-500 text-green-700', soft: 'bg-green-100 text-green-700', solid: 'bg-emerald-600', ring: 'focus:ring-green-500', btn: 'bg-green-600 hover:bg-green-700' }
const BRONZE = { navOn: 'border-[#8A7044] text-gray-900', mobOn: 'bg-amber-50 border-[#8A7044] text-[#5C4924]', soft: 'bg-amber-100 text-[#5C4924]', solid: 'bg-[#7A6234]', ring: 'focus:ring-[#8A7044]', btn: 'bg-[#7A6234] hover:bg-[#6a552c]' }
export function useMenuColors() {
  const { beta } = useUiBeta()
  return computed(() => (beta.value ? BRONZE : GREEN))
}
